import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, Lock, Unlock, Info, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MODE_TEXT = { snapshot: 'from its saved pre-cancellation state', inferred: 'inferred from its workflow fields', fallback: 'fallback — prior stage could not be determined' };

/* ORDER-RESTORE-STATE-001 — Restore (exact prior stage) vs Restore & Reopen for Rework (existing audited Reopen). */
export const RestoreOrderModal = ({ order, onClose, onRestored }) => {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [rework, setRework] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    axios.get(`${API}/api/orders/${order.id}/restore-preview`, auth)
      .then((r) => setPreview(r.data))
      .catch((e) => setPreviewError(e?.response?.data?.detail || 'Could not determine the previous stage'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const submit = async () => {
    if (rework && reason.trim().length < 10) { setError('Reopen reason must be at least 10 characters'); return; }
    setBusy(true); setError('');
    try {
      const r = rework
        ? await axios.post(`${API}/api/orders/${order.id}/restore-and-reopen`, { reopen_reason: reason.trim() }, auth)
        : await axios.patch(`${API}/api/orders/${order.id}/restore`, {}, auth);
      toast.success(r.data?.message || 'Order restored', { duration: 4000 });
      onRestored?.(r.data?.order);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Failed to restore order');
    } finally { setBusy(false); }
  };

  const reopenAvailable = !!preview?.reopen_available;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="restore-order-modal">
        <div className="px-6 py-4 border-b border-gray-200 bg-emerald-50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center"><RotateCcw className="w-5 h-5 text-emerald-600" /></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Restore Order</h3>
            <p className="text-xs text-gray-500">{order.order_number || order.orderNumber}</p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {previewError && <p className="text-sm text-red-600" data-testid="restore-preview-error">{previewError}</p>}
          {!preview && !previewError && <p className="text-sm text-gray-500">Determining previous stage…</p>}
          {preview && (
            <div className={`rounded-lg border p-3 ${preview.mode === 'fallback' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="restore-preview">
              <div className="flex items-center gap-2 text-sm">
                {preview.mode === 'fallback' ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <Info className="w-4 h-4 text-emerald-600" />}
                <span className="text-gray-800">Previous stage: <strong data-testid="restore-preview-stage">{preview.label}</strong></span>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-500">
                  {preview.is_locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}{preview.is_locked ? 'locked' : 'editable'}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Determined {MODE_TEXT[preview.mode]}.{preview.note ? ` ${preview.note}` : ''}</p>
              {preview.cancellation_reason && <p className="text-xs text-gray-500 mt-1">Cancellation reason: “{preview.cancellation_reason}”</p>}
            </div>
          )}

          <div className="space-y-2">
            <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${!rework ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200'}`}>
              <input type="radio" name="restore-mode" className="mt-0.5" checked={!rework} onChange={() => setRework(false)} data-testid="restore-mode-restore" />
              <span>
                <span className="block text-sm font-semibold text-gray-900">Restore</span>
                <span className="block text-xs text-gray-600">Return the order to its exact pre-cancellation stage{preview ? ` (${preview.label})` : ''}. Completed orders stay completed.</span>
              </span>
            </label>
            <label className={`flex items-start gap-3 rounded-lg border p-3 ${reopenAvailable ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'} ${rework ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200'}`}>
              <input type="radio" name="restore-mode" className="mt-0.5" disabled={!reopenAvailable} checked={rework} onChange={() => setRework(true)} data-testid="restore-mode-rework" />
              <span>
                <span className="block text-sm font-semibold text-gray-900">Restore &amp; Reopen for Rework</span>
                <span className="block text-xs text-gray-600">
                  {reopenAvailable
                    ? 'Restore to Completed, then run the audited Reopen for Edit (unlock, clear completion, unassign) so the order can be worked again.'
                    : 'Only available when the previous stage is Completed — other stages return directly to their working queue.'}
                </span>
              </span>
            </label>
          </div>

          {rework && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reopen reason <span className="text-red-500">*</span></label>
              <textarea value={reason} onChange={(e) => { setReason(e.target.value); setError(''); }} rows={2} maxLength={500}
                placeholder="Why does this order need rework after restoration? (min 10 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                data-testid="restore-rework-reason" />
            </div>
          )}
          {error && <p className="text-sm text-red-600" data-testid="restore-error">{error}</p>}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white font-medium disabled:opacity-50" data-testid="restore-modal-dismiss-btn">Go Back</button>
          <button onClick={submit} disabled={busy || !preview}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 flex items-center gap-2" data-testid="confirm-restore-order-btn">
            {busy ? 'Restoring…' : rework ? 'Restore & Reopen' : `Restore${preview ? ` to ${preview.label}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestoreOrderModal;
